/**
 * All monetary values in this application are integer **cents**. Never floats.
 * Rounding follows the Austrian commercial convention (kaufmännisch, half away
 * from zero), which is what the Finanzamt expects on invoices and the UVA.
 */
const CENT_FACTOR = 100;
/** Kaufmännische Rundung: 0.5 rounds away from zero, so -2.5 -> -3. */
export function roundHalfAwayFromZero(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
/** 12.345 EUR -> 1235 cents (kaufmännisch gerundet). */
export function toCents(euros) {
    return roundHalfAwayFromZero(euros * CENT_FACTOR);
}
/** 1235 cents -> 12.35 EUR. Only use for display or for external APIs. */
export function toEuros(cents) {
    return cents / CENT_FACTOR;
}
/** Multiply cents by a factor (rate, quantity, share) and round to whole cents. */
export function mulCents(cents, factor) {
    return roundHalfAwayFromZero(cents * factor);
}
/** VAT on a net amount, e.g. vatFromNet(10000, 0.2) === 2000. */
export function vatFromNet(net, rate) {
    return mulCents(net, rate);
}
/** Net amount contained in a gross amount, e.g. netFromGross(12000, 0.2) === 10000. */
export function netFromGross(gross, rate) {
    return roundHalfAwayFromZero(gross / (1 + rate));
}
/** VAT contained in a gross amount. Derived from the net so net + vat === gross exactly. */
export function vatFromGross(gross, rate) {
    return gross - netFromGross(gross, rate);
}
export function sumCents(values) {
    let total = 0;
    for (const value of values)
        total += value;
    return total;
}
/**
 * Split an amount into `parts` shares that add up exactly to the original.
 * The remainder cents are handed to the first shares (largest-remainder free,
 * deterministic and audit-safe).
 */
export function splitCents(total, parts) {
    if (parts <= 0)
        return [];
    const base = Math.trunc(total / parts);
    let remainder = total - base * parts;
    const step = remainder < 0 ? -1 : 1;
    return Array.from({ length: parts }, () => {
        if (remainder !== 0) {
            remainder -= step;
            return base + step;
        }
        return base;
    });
}
const DEFAULT_LOCALE = 'de-AT';
export function formatCents(cents, currency = 'EUR', locale = DEFAULT_LOCALE) {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(toEuros(cents));
}
export function formatCentsPlain(cents, locale = DEFAULT_LOCALE) {
    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(toEuros(cents));
}
/**
 * Cents as a plain machine-readable decimal string: `1235 -> "12.35"`,
 * `-5 -> "-0.05"`. For CSV exports and the EN 16931 e-invoice XML, where the
 * decimal point and the digit count are part of the format, not a locale
 * choice — unlike `formatCents`/`formatCentsPlain`, which are for people.
 *
 * Built from integer arithmetic rather than `(cents / 100).toFixed(2)`: a
 * float division can land one ULP off the exact value, and `toFixed` then
 * rounds that drifted float instead of the cent amount that was actually
 * meant.
 */
export function toMachineDecimal(cents) {
    const sign = cents < 0 ? '-' : '';
    const whole = Math.trunc(Math.abs(cents));
    const euros = Math.trunc(whole / CENT_FACTOR);
    const rest = String(whole % CENT_FACTOR).padStart(2, '0');
    return `${sign}${euros}.${rest}`;
}
/**
 * Parse user input the Austrian way: "1.234,56" and "1234.56" both mean 1234.56.
 * Returns null when the string carries no parseable number.
 */
export function parseAmountToCents(input) {
    // de-AT formatting uses a narrow no-break space as the thousands separator,
    // so pasted amounts have to be stripped of it explicitly.
    const trimmed = input.trim().replace(/[\s\u00A0\u202F\u2007€]/g, '');
    if (trimmed === '')
        return null;
    const lastComma = trimmed.lastIndexOf(',');
    const lastDot = trimmed.lastIndexOf('.');
    let normalised;
    if (lastComma > lastDot) {
        // German notation: dots are thousands separators, comma is the decimal mark.
        normalised = trimmed.replace(/\./g, '').replace(',', '.');
    }
    else if (lastDot > lastComma) {
        normalised = trimmed.replace(/,/g, '');
    }
    else {
        normalised = trimmed;
    }
    const value = Number(normalised);
    return Number.isFinite(value) ? toCents(value) : null;
}
/** Convert a foreign-currency amount into EUR cents using an FX rate (foreign per 1 EUR). */
export function convertToEur(amount, rateForeignPerEur) {
    if (!rateForeignPerEur || rateForeignPerEur <= 0)
        return amount;
    return roundHalfAwayFromZero(amount / rateForeignPerEur);
}
//# sourceMappingURL=money.js.map